import axios from "axios";

export const ERROR_MESSAGES = {
  COMMUNICATION_UNAUTHORIZED: "Bitte melden Sie sich an, um den Chat zu nutzen.",
  COMMUNICATION_FORBIDDEN: "Sie haben keine Berechtigung für diese Aktion.",
  TENANT_MISMATCH: "Zugriff auf einen anderen Mandanten verweigert.",
  MEMBERSHIP_REQUIRED: "Sie sind kein Teilnehmer dieser Konversation.",
  MEMBERSHIP_INACTIVE: "Ihre Konversationsteilnahme ist nicht mehr aktiv.",
  MESSAGE_EDIT_FORBIDDEN: "Sie können nur Ihre eigenen Nachrichten bearbeiten.",
  MESSAGE_DELETE_FORBIDDEN: "Sie sind nicht berechtigt, diese Nachricht zu löschen.",
  CONVERSATION_NOT_FOUND: "Die Konversation wurde nicht gefunden.",
  MESSAGE_NOT_FOUND: "Die Nachricht wurde nicht gefunden.",
  CONVERSATION_CLOSED: "Diese Konversation ist geschlossen und kann keine Nachrichten empfangen.",
  IDEMPOTENCY_KEY_REUSED: "Nachricht wurde bereits mit anderem Inhalt übermittelt.",
  INVALID_MESSAGE: "Ungültiger Nachrichteninhalt.",
  COMMUNICATION_ERROR: "Ein unerwarteter Fehler ist aufgetreten."
};

export function mapCommunicationError(err) {
  if (!err) return "Unbekannter Fehler";
  const code = err.response?.data?.error?.code || err.code;
  if (code && ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code];
  }
  const backendMsg = err.response?.data?.error?.message;
  if (
    backendMsg &&
    typeof backendMsg === "string" &&
    !/mongo|mongoose|connect|stack|node_modules/i.test(backendMsg)
  ) {
    return backendMsg;
  }
  return "Ein Kommunikationsfehler ist aufgetreten.";
}

export class CommunicationApiClient {
  constructor({
    baseUrl = process.env.REACT_APP_BACKEND_URL || "",
    getToken = () => (typeof localStorage !== "undefined" ? localStorage.getItem("token") : null),
    httpClient = axios
  } = {}) {
    let cleanBase = baseUrl ? baseUrl.replace(/\/+$/, "") : "";
    if (cleanBase.endsWith("/api")) {
      cleanBase = cleanBase.slice(0, -4);
    }
    this.baseUrl = cleanBase;
    this.getToken = getToken;
    this.httpClient = httpClient;
  }

  getHeaders() {
    const token = this.getToken ? this.getToken() : null;
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  }

  async listConversations({ limit = 50 } = {}) {
    const res = await this.httpClient.get(
      `${this.baseUrl}/api/communication/conversations`,
      { params: { limit }, headers: this.getHeaders() }
    );
    return res.data;
  }

  async createDirectConversation({ participantIdentityId, context = { type: "NONE" } }) {
    if (!participantIdentityId) {
      throw new Error("participantIdentityId is required");
    }
    const res = await this.httpClient.post(
      `${this.baseUrl}/api/communication/conversations/direct`,
      { participantIdentityId, context },
      { headers: this.getHeaders() }
    );
    return res.data;
  }

  async createGroupConversation({
    participantIdentityIds,
    context = { type: "NONE" },
    metadata = {}
  }) {
    if (!Array.isArray(participantIdentityIds) || participantIdentityIds.length === 0) {
      throw new Error("participantIdentityIds must be a non-empty array");
    }
    const res = await this.httpClient.post(
      `${this.baseUrl}/api/communication/conversations/group`,
      { participantIdentityIds, context, metadata },
      { headers: this.getHeaders() }
    );
    return res.data;
  }

  async listMessages(conversationId, { beforeSentAt = null, beforeId = null, limit = 30 } = {}) {
    if (!conversationId) throw new Error("conversationId is required");
    const params = { limit };
    if (beforeSentAt) {
      params.beforeSentAt =
        beforeSentAt instanceof Date ? beforeSentAt.toISOString() : beforeSentAt;
    }
    if (beforeId) {
      params.beforeId = beforeId;
    }

    const res = await this.httpClient.get(
      `${this.baseUrl}/api/communication/conversations/${conversationId}/messages`,
      { params, headers: this.getHeaders() }
    );
    return res.data;
  }

  async sendMessage(conversationId, { idempotencyKey, text }) {
    if (!conversationId) throw new Error("conversationId is required");
    if (!idempotencyKey) throw new Error("idempotencyKey is required");
    if (!text || !text.trim()) throw new Error("text is required");

    const res = await this.httpClient.post(
      `${this.baseUrl}/api/communication/conversations/${conversationId}/messages`,
      {
        idempotencyKey,
        payload: {
          type: "TEXT",
          text: text.trim()
        }
      },
      { headers: this.getHeaders() }
    );
    return res.data;
  }

  async editMessage(conversationId, messageId, text) {
    if (!conversationId || !messageId) {
      throw new Error("conversationId and messageId are required");
    }
    if (!text || !text.trim()) throw new Error("text is required");

    const res = await this.httpClient.patch(
      `${this.baseUrl}/api/communication/conversations/${conversationId}/messages/${messageId}`,
      {
        payload: {
          type: "TEXT",
          text: text.trim()
        }
      },
      { headers: this.getHeaders() }
    );
    return res.data;
  }

  async deleteMessage(conversationId, messageId) {
    if (!conversationId || !messageId) {
      throw new Error("conversationId and messageId are required");
    }

    const res = await this.httpClient.delete(
      `${this.baseUrl}/api/communication/conversations/${conversationId}/messages/${messageId}`,
      { headers: this.getHeaders() }
    );
    return res.data;
  }

  async markRead(conversationId, messageId = null) {
    if (!conversationId) throw new Error("conversationId is required");

    const res = await this.httpClient.post(
      `${this.baseUrl}/api/communication/conversations/${conversationId}/read`,
      { messageId },
      { headers: this.getHeaders() }
    );
    return res.data;
  }
}

export const communicationApi = new CommunicationApiClient({
  baseUrl: process.env.REACT_APP_BACKEND_URL || ""
});
